import { Component, inject } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { map, Observable, Subscription } from 'rxjs';
import { environment } from '../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';

type PlayerError = {
  code: string;
  title: string;
  message: string;
};

@Component({
  selector: 'app-error-page',
  imports: [],
  standalone: true,
  template: `
    @if (error) {
      <div class="error-container">
        <h1 class="error-title">{{ error.code }} / {{ error.title }}</h1>
        <p class="error-message">{{ error.message }}</p>
      </div>
    }
  `,
  styles: `
    :host {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      width: 100vw;
    }
  `,
})
export class ErrorPage {
  route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private http: HttpClient = inject(HttpClient);
  errors: PlayerError[] = [
    {
      code: '404',
      title: 'Page Not Found',
      message: 'Oops! The page you are looking for does not exist.',
    },
    {
      code: '500',
      title: 'Internal Server Error',
      message: 'Sorry! Something went wrong on our end.',
    },
    {
      code: '403',
      title: 'Access Denied',
      message: 'You do not have permission to access this page.',
    },
  ];
  // undefined tant qu'on n'a pas déterminé qu'il s'agit d'une véritable erreur : une URL de hash
  // héritée est en cours de résolution (redirection vers /scenic/:id ou /coaching/:id) et ne doit
  // jamais afficher de 404 le temps de cette résolution, aussi lente soit-elle.
  error: PlayerError | undefined = undefined;
  subcriptions: Subscription[] = [];

  private getContentId(contentType: 'coaching' | 'scenic', hashId: string): Observable<number> {
    const endpointType: string =
      contentType === 'coaching' ? 'coaching/coachingByHashId' : 'scenic/videoByHashId';
    const endpoint: string = `${environment.KINOMAP_API_URL}/${endpointType}`;
    const params: HttpParams = new HttpParams()
      .set('hash_id', hashId)
      .set('appToken', environment.API_APP_TOKEN);
    return this.http.get<any>(endpoint, { params }).pipe(map((response: any) => response.data.id));
  }

  ngOnInit(): void {
    const hashId: string | null = this.route.snapshot.paramMap.get('hashId');

    if (hashId) {
      // Legacy hash URL (`/gnzbts`) : on résout la redirection avant d'afficher quoi que ce soit,
      // pour éviter un flash de 404 le temps de l'appel réseau.
      const type: 'coaching' | 'scenic' = hashId.length === 7 ? 'coaching' : 'scenic';
      this.subcriptions.push(
        this.getContentId(type, hashId).subscribe({
          next: (id: number) => this.router.navigate([type, id]),
          error: () => {
            this.error = this.errors.find((err: PlayerError) => err.code === '404') || this.errors[0];
          },
        }),
      );
      return;
    }

    this.subcriptions.push(
      this.route.queryParams.subscribe((params: Params) => {
        const errorCode: string | undefined = params['code'];
        this.error = this.errors.find((err: PlayerError) => err.code === errorCode) || this.errors[0];
      }),
    );
  }

  ngOnDestroy(): void {
    this.subcriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }
}
